import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  BarChart3,
  BookOpenCheck,
  Database,
  ExternalLink,
  FileInput,
  FileText,
  Layers,
  Loader2,
  PlusCircle,
  Users,
} from 'lucide-react'
import DashboardSidebar from '../lesson-builder/components/DashboardSidebar'
import { API_CONFIG } from '../../config/api.config'

const PMAKER_ORIGIN = 'https://pmaker.vatli365.vn'
const token = () => localStorage.getItem('jwt_token') || localStorage.getItem('auth_token') || localStorage.getItem('token') || ''

type DashboardStats = {
  total_questions?: number
  total_exams?: number
  total_attempts?: number
  total_students?: number
  recent_exams?: Array<{ title: string; slug?: string; created_at?: string }>
}

type MyExam = {
  id: string
  title: string
  slug?: string
  created_at?: string
  is_published?: boolean
  attempts_count?: number
  exam_link?: string
}

async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_CONFIG.python.apiUrl}${path}`, {
    headers: { Authorization: `Bearer ${token()}` },
  })
  if (!res.ok) throw new Error(`API ${res.status}`)
  return res.json()
}

// /pmaker-advanced/my-exams trả về { success, data: [...] } — chuẩn hoá về mảng để tránh .slice lỗi
function asExamArray(v: any): MyExam[] {
  if (Array.isArray(v)) return v
  if (Array.isArray(v?.data)) return v.data
  if (Array.isArray(v?.exams)) return v.exams
  if (Array.isArray(v?.items)) return v.items
  return []
}

const quickActions = [
  {
    title: 'Số hoá đề Word/PDF',
    desc: 'Mở Studio số hoá của PMaker để tách câu, công thức, ảnh.',
    href: `${PMAKER_ORIGIN}/digitize`,
    icon: FileInput,
    color: 'blue',
  },
  {
    title: 'Ra đề ngẫu nhiên',
    desc: 'Sinh đề theo ma trận và ngân hàng câu hỏi Vật lí.',
    href: `${PMAKER_ORIGIN}/generator`,
    icon: Layers,
    color: 'purple',
  },
  {
    title: 'Quản lý đề thi',
    desc: 'Xem, xuất Word/PDF, cài đặt đề và lấy link thi online.',
    href: `${PMAKER_ORIGIN}/exams`,
    icon: FileText,
    color: 'emerald',
  },
  {
    title: 'Ngân hàng câu hỏi',
    desc: 'Quản lý câu hỏi cá nhân/chia sẻ dùng để sinh đề.',
    href: `${PMAKER_ORIGIN}/bank`,
    icon: Database,
    color: 'amber',
  },
  {
    title: 'Giao bài cho lớp',
    desc: 'Mở luồng giao đề PMaker cho danh sách học sinh.',
    href: `${PMAKER_ORIGIN}/classes`,
    icon: Users,
    color: 'rose',
  },
  {
    title: 'Dùng trong bài giảng ebook',
    desc: 'Chèn đề PMaker vào thẻ Trắc nghiệm của bài giảng.',
    to: '/soan-bai',
    icon: BookOpenCheck,
    color: 'sky',
  },
]

const colorClass: Record<string, string> = {
  blue: 'bg-blue-50 text-blue-700 border-blue-100',
  purple: 'bg-purple-50 text-purple-700 border-purple-100',
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  amber: 'bg-amber-50 text-amber-700 border-amber-100',
  rose: 'bg-rose-50 text-rose-700 border-rose-100',
  sky: 'bg-sky-50 text-sky-700 border-sky-100',
}

const PmakerBridgePage: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [exams, setExams] = useState<MyExam[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    Promise.allSettled([
      apiGet<DashboardStats>('/pmaker-bank/dashboard-stats'),
      apiGet<any>('/pmaker-advanced/my-exams'),
    ]).then(([statsRes, examsRes]) => {
      if (!alive) return
      if (statsRes.status === 'fulfilled') setStats(statsRes.value)
      if (examsRes.status === 'fulfilled') setExams(asExamArray(examsRes.value))
      if (statsRes.status === 'rejected' && examsRes.status === 'rejected') {
        setError('Chưa đọc được số liệu PMaker. Các lối tắt bên dưới vẫn dùng được.')
      }
      setLoading(false)
    })
    return () => { alive = false }
  }, [])

  const statCards = [
    { label: 'Câu hỏi', value: stats?.total_questions ?? '—' },
    { label: 'Đề đã tạo', value: stats?.total_exams ?? exams.length ?? '—' },
    { label: 'Lượt làm', value: stats?.total_attempts ?? '—' },
    { label: 'Học sinh', value: stats?.total_students ?? '—' },
  ]

  return (
    <div className="flex min-h-screen bg-[#F8FAFC]">
      <DashboardSidebar />
      <main className="flex-1 p-6">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-500">Ebook Dashboard / PMaker</p>
            <h1 className="mt-1 text-2xl font-black text-slate-900">PMaker trong ebook</h1>
            <p className="mt-1 max-w-3xl text-sm text-slate-500">
              Trang này chỉ ánh xạ các chức năng chính của PMaker vào dashboard giáo viên ebook.
              Dữ liệu và thao tác gốc vẫn giữ nguyên ở PMaker.
            </p>
          </div>
          <a
            href={PMAKER_ORIGIN}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-xl border border-blue-100 bg-white px-4 py-2.5 text-sm font-bold text-blue-700 shadow-sm hover:bg-blue-50"
          >
            Mở PMaker đầy đủ <ExternalLink size={16} />
          </a>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {statCards.map((it) => (
            <div key={it.label} className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{it.label}</p>
              <p className="mt-2 text-2xl font-black text-slate-900">{loading ? <Loader2 className="animate-spin" size={22} /> : it.value}</p>
            </div>
          ))}
        </div>

        {error && (
          <div className="mb-5 rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-700">
            {error}
          </div>
        )}

        <section className="mb-8">
          <div className="mb-3 flex items-center gap-2">
            <PlusCircle className="text-blue-600" size={18} />
            <h2 className="text-lg font-black text-slate-900">Tính năng chính</h2>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {quickActions.map((action) => {
              const Icon = action.icon
              const body = (
                <>
                  <div className={`mb-4 inline-flex h-11 w-11 items-center justify-center rounded-2xl border ${colorClass[action.color]}`}>
                    <Icon size={21} />
                  </div>
                  <h3 className="text-base font-black text-slate-900">{action.title}</h3>
                  <p className="mt-1 text-sm text-slate-500">{action.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-blue-700">
                    Mở chức năng <ExternalLink size={14} />
                  </span>
                </>
              )
              return action.to ? (
                <Link key={action.title} to={action.to} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
                  {body}
                </Link>
              ) : (
                <a key={action.title} href={action.href} target="_blank" rel="noreferrer" className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
                  {body}
                </a>
              )
            })}
          </div>
        </section>

        <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <BarChart3 className="text-emerald-600" size={18} />
              <h2 className="text-lg font-black text-slate-900">Đề PMaker gần đây</h2>
            </div>
            <a href={`${PMAKER_ORIGIN}/exams`} target="_blank" rel="noreferrer" className="text-sm font-bold text-blue-700 hover:underline">
              Quản lý tất cả
            </a>
          </div>
          {loading ? (
            <div className="flex items-center gap-2 py-6 text-sm font-semibold text-slate-400">
              <Loader2 className="animate-spin" size={18} /> Đang tải dữ liệu PMaker...
            </div>
          ) : exams.length === 0 ? (
            <p className="py-6 text-sm text-slate-500">Chưa có đề PMaker hoặc chưa đọc được danh sách đề.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {exams.slice(0, 6).map((ex) => (
                <div key={ex.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                  <div>
                    <p className="font-bold text-slate-900">{ex.title || 'Đề chưa đặt tên'}</p>
                    <p className="text-xs text-slate-400">
                      {ex.is_published ? 'Đã xuất bản' : 'Bản nháp'} · {ex.attempts_count || 0} lượt làm
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {ex.slug && (
                      <Link to={`/exam/${ex.slug}`} className="rounded-lg bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700 hover:bg-emerald-100">
                        Xem đề
                      </Link>
                    )}
                    <a href={`${PMAKER_ORIGIN}/exams`} target="_blank" rel="noreferrer" className="rounded-lg bg-blue-50 px-3 py-2 text-xs font-bold text-blue-700 hover:bg-blue-100">
                      Mở PMaker
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  )
}

export default PmakerBridgePage
